import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EmptyState } from '@/components/molecules/EmptyState';
import type {
    LocationProviderConfig,
    ProviderConfigError,
    ProviderGroup,
    ProviderSchema,
} from '@/domain/providerConfig';
import { parseLocale } from '@/utils/localeFormatters';

import { AddProviderModal } from './components/AddProviderModal';
import { ProviderCard } from './components/ProviderCard';
import { UnsavedChangesGuard } from './components/UnsavedChangesGuard';

import styles from './DataSources.module.scss';

export interface DataSourcesViewProps {
    config: LocationProviderConfig;
    schemas: ProviderSchema[];
    isOptoScaleAdmin: boolean;
    refetch: () => Promise<LocationProviderConfig | null>;
    /** A refetch failed after the page loaded; the cards keep showing the rows they have. */
    refreshError: ProviderConfigError | null;
}

interface CardEntry {
    schema: ProviderSchema;
    group: ProviderGroup | null;
}

function refreshErrorKey(error: ProviderConfigError): string {
    if (error.kind === 'unauthenticated') return 'providerConfig.errors.sessionExpired';
    if (error.kind === 'forbidden') return 'providerConfig.errors.forbidden';
    return 'providerConfig.page.refreshFailed';
}

function DataSourcesView({
    config,
    schemas,
    isOptoScaleAdmin,
    refetch,
    refreshError,
}: DataSourcesViewProps) {
    const { t, i18n } = useTranslation();
    const [added, setAdded] = useState<string[]>([]);
    const [adding, setAdding] = useState(false);
    const [dirtyCards, setDirtyCards] = useState<Record<string, boolean>>({});
    const onDirtyChange = useCallback((providerName: string, dirty: boolean) => {
        setDirtyCards((previous) =>
            previous[providerName] === dirty ? previous : { ...previous, [providerName]: dirty }
        );
    }, []);

    const schemaOf = (name: string): ProviderSchema | undefined =>
        schemas.find((schema) => schema.providerName === name);
    const atSite = new Set(config.providers.map((group) => group.providerName));
    // Another admin removed the last row of a provider whose card holds unsaved edits: the card
    // stays, with no rows, so the edits and the leave guard survive and the card says so.
    const vanished = Object.keys(dirtyCards).filter(
        (name) => dirtyCards[name] && !atSite.has(name) && !added.includes(name)
    );
    // A card added here becomes a configured one once saved; its key keeps it mounted.
    const cards: CardEntry[] = [
        ...config.providers.map((group) => ({ schema: schemaOf(group.providerName), group })),
        ...vanished.map((name) => ({ schema: schemaOf(name), group: null })),
        ...added
            .filter((name) => !atSite.has(name))
            .map((name) => ({ schema: schemaOf(name), group: null })),
    ].flatMap((entry) => (entry.schema ? [{ schema: entry.schema, group: entry.group }] : []));
    const addable = schemas
        .map((schema) => schema.providerName)
        .filter((name) => !atSite.has(name) && !added.includes(name));
    const list = new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' });
    const penName = (cageId: number): string =>
        config.pens.find((pen) => pen.cageId === cageId)?.name ?? String(cageId);

    return (
        <PageLayout
            title={t('providerConfig.page.title')}
            actions={
                addable.length > 0 && (
                    <Button variant="secondary" onClick={() => setAdding(true)}>
                        {t('providerConfig.page.addProvider')}
                    </Button>
                )
            }>
            <UnsavedChangesGuard when={Object.values(dirtyCards).some(Boolean)} />
            {/* Under the header, not beside the title: inline it wrapped the h1 and, at 390,
                set a five-line sentence next to it. */}
            <p className={styles.intro}>{t('providerConfig.page.intro')}</p>
            {/* Always mounted: a live region added together with its text is not announced. */}
            <div aria-live="polite">
                {refreshError !== null && (
                    <Alert tone="warning">{t(refreshErrorKey(refreshError))}</Alert>
                )}
            </div>
            <div className={styles.cards}>
                {cards.length === 0 && (
                    <ContentBlock spacing="none">
                        <EmptyState title={t('providerConfig.page.empty')} size="spacious" />
                    </ContentBlock>
                )}
                {cards.map(({ schema, group }) => (
                    <ProviderCard
                        key={schema.providerName}
                        schema={schema}
                        group={group}
                        pens={config.pens}
                        locationId={config.locationId}
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        refetch={refetch}
                        onDirtyChange={onDirtyChange}
                    />
                ))}
                {config.unsupported.length > 0 && (
                    // A note that was there on load, so it is not announced.
                    <Alert
                        tone="info"
                        live="off"
                        icon={<SvgIcon icon={Info} size="medium" color="currentColor" />}>
                        <ul className={styles.unsupported}>
                            {config.unsupported.map((entry) => (
                                <li key={entry.providerName} className={styles.note}>
                                    {t('providerConfig.page.unsupported', {
                                        // An unsupported provider usually has no label, so its
                                        // stored identifier stands in.
                                        provider: t(
                                            `providerConfig.providers.${entry.providerName}`,
                                            {
                                                defaultValue: entry.providerName,
                                            }
                                        ),
                                        pens: list.format(entry.cageIds.map(penName)),
                                    })}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}
            </div>
            <AddProviderModal
                isOpen={adding}
                providerNames={addable}
                onAdd={(name) => {
                    setAdded((previous) => [...previous, name]);
                    setAdding(false);
                }}
                onClose={() => setAdding(false)}
            />
        </PageLayout>
    );
}

export default DataSourcesView;
export { DataSourcesView };
